import * as React from "react";
import {connect} from "react-redux";
import {OnFailure, OnSuccess, OnSuccessData} from "../../../../../store/action/actionHelpers";
import {Dispatch} from "redux";
import Toast from "../../../../helper/Toast";
import Asset from "../../../../../model/asset/Asset";
import EditAssetFormView from "./EditAssetFormView";
import {addOrUpdateAssetAttachments, fetchAsset, updateAsset} from "../../../../../store/action/asset/asset";
import {none, Option, option} from "ts-option";
import {RouteComponentProps, withRouter} from "react-router";
import {RootState} from "../../../../../store/reducer/root/RootState";
import Loading from "../../../../ui/loading/Loading";
import Routes from "../../../../../navigation/route/Route";
import {notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import {getAsset} from "../../../../../store/view/asset";
import Attachment from "../../../../../model/attachment/Attachment";
import AssetUploadHelper from "../../../../../helper/AssetUploadHelper";

interface RouteProps {
	/**
	 * Asset identifier.
	 */
	assetId: string
}

interface IDispatchProps {
	/**
	 * Update asset.
	 * @param {asset} asset
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateAsset: (asset: Asset, onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Add attachments to asset
	 * @param {Asset} asset
	 */
	addOrUpdateAssetAttachments: (asset: Asset, attachments: Attachment[], onSuccess: OnSuccess, onFailure: OnFailure) => void;
	/**
	 * Fetch asset
	 */
	fetchAsset: (assetId: string, onSuccess: OnSuccessData<Asset>, onFailure: OnFailure) => void
}

interface IState {
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
	/**
	 * Image file of asset
	 */
	files: Option<(File | Attachment)[]>
	/**
	 * Updated asset to submit.
	 */
	assetToSubmit: Option<Asset>;
}

interface ReduxProps {
	asset: Option<Asset>
}

type Props = IDispatchProps & RouteComponentProps<RouteProps> & ReduxProps;

class EditAssetForm extends React.PureComponent<Props, IState> {
	constructor(props: Props) {
		super(props);
		this.state = {
			isSubmitting: false,
			files: none,
			assetToSubmit: props.asset
		};
		this.onSubmit = this.onSubmit.bind(this);
		this.onUpdateAsset = this.onUpdateAsset.bind(this);
		this.onAddOrRemoveFiles = this.onAddOrRemoveFiles.bind(this);
		this.onSubmitSuccess = this.onSubmitSuccess.bind(this);
		this.onSubmitFailure = this.onSubmitFailure.bind(this);
		this.onCancel = this.onCancel.bind(this);
	}

	componentDidMount() {
		this.props.fetchAsset(this.props.match.params.assetId,
			asset => this.setState({assetToSubmit: option(asset), files: option(asset.attachments)}),
			(e) => {
				notifyBugsnagAndShowToast(e, "Fetch asset");
				this.onCancel()
			})
	}

	render() {
		return (
			<React.Fragment>
				<Loading isLoading={this.props.asset.isEmpty}>
					{this.props.asset.map(a =>
						<EditAssetFormView onSubmit={this.onUpdateAsset}
										   onCancel={this.onCancel}
										   isSubmitting={this.state.isSubmitting}
										   onAddOrUpdateFile={this.onAddOrRemoveFiles}
										   asset={a}/>
					).orNull}
				</Loading>
			</React.Fragment>
		);
	}

	onCancel() {
		this.props.history.push(Routes.AssetList);
	}

	onAddOrRemoveFiles(files: (File | Attachment)[]) {
		if (files.length > 0) {
			this.setState({files: option(files)})
		}
		else {
			this.setState({files: none})
		}
	}

	onSubmit() {
		this.setState({isSubmitting: true}, () => {
			const asset = this.state.assetToSubmit.get.copy({id: this.props.asset.map(a => a.id).getOrElse("")});
			this.props.updateAsset(
				asset,
				() => {
					this.state.files.map((files: (File | Attachment)[]) => {
						AssetUploadHelper.uploadAssetFilesAndAttachments(files)
							.then(attachments => {
								const assetAttachements = this.state.assetToSubmit.map(a => a.attachments).getOrElse([])
								if (assetAttachements.length != attachments.length || !attachments.every(
									(item, index) => assetAttachements[index] && assetAttachements[index].id == item.id)) {
									this.props.addOrUpdateAssetAttachments(asset, attachments, this.onSubmitSuccess,
										this.onSubmitFailure)
								}
								else {
									this.onSubmitSuccess()
								}
							})
							.catch(this.onSubmitFailure);
					}).getOrElse(() => {
						if (this.props.asset.map(a => a.attachments.length > 0).getOrElse(false)) {
							this.props.addOrUpdateAssetAttachments(asset, [], this.onSubmitSuccess,
								this.onSubmitFailure)
						}
						else {
							this.onSubmitSuccess()
						}
					});
				},
				(error) => {
					let errorText = "Asset update failed";
					if (error.response.data && error.response.data.exception === "AssetCategoryDisabledException") {
						errorText = "Update rejected because asset category is disabled";
					}
					this.onSubmitFailure(errorText)
				}
			);
		});
	}

	onUpdateAsset(editedAsset: Asset) {
		this.setState({assetToSubmit: option(editedAsset)}, this.onSubmit);
	}

	onSubmitSuccess() {
		this.setState({isSubmitting: false}, () => {
			Toast.success("Asset updated", "Asset update");
			this.props.history.push(Routes.AssetList);
		});
	}

	onSubmitFailure(text: string) {
		this.setState({isSubmitting: false}, () => Toast.error(text, "Asset update"));
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateAsset: (asset: Asset, onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(updateAsset(asset, onSuccess, onFailure)),
		addOrUpdateAssetAttachments: (asset: Asset, attachments: Attachment[], onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(addOrUpdateAssetAttachments(asset, attachments, onSuccess, onFailure)),
		fetchAsset: (assetId: string, onSuccess: OnSuccessData<Asset>, onFailure: OnFailure) => dispatch(
			fetchAsset(assetId, onSuccess, onFailure))
	};
}

function mapStateToProps(state: RootState, props: Props) {
	const asset = getAsset(props.match.params.assetId);
	return {asset};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditAssetForm));
